/**
 * accessoryRowIds.js: IDI-442 (scope v2, 2026-10-14).
 *
 * Each accessory ROW on a line has its own stable id, from the moment it is
 * picked. Quantity edits and delete act on that row id only.
 *
 * Before: the picker committed rows as `{accessory_id, name, ...}` with NO
 * `id`, and InlineAccessoryRows updated/removed by `a.id === id`. Every
 * picked row had `id === undefined`, so a quantity typed on one row was
 * written to all of them and the trash button removed all of them. Not the
 * catalog `accessory_id` either: two rows can be the same catalog item.
 *
 * Saved rows that predate this get an id on load: `<line id>:acc:<index>`,
 * so an existing quote behaves the same and the id is stable across reloads.
 */

let seq = 0;

/** A fresh row id for a newly picked accessory. */
export function newAccessoryRowId() {
  seq += 1;
  return `acc-${Date.now().toString(36)}-${seq.toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
}

/**
 * Give every row a unique id. Rows that already have a unique, non-empty id
 * keep it; a missing or duplicated id is replaced. Deterministic when `lineId`
 * is given (load path), fresh otherwise (pick path). Returns a NEW array only
 * when something changed, so React memo/dirty checks don't churn.
 */
export function ensureAccessoryRowIds(accessories, lineId = null) {
  if (!Array.isArray(accessories) || accessories.length === 0) return accessories;
  const seen = new Set();
  let changed = false;
  const out = accessories.map((a, i) => {
    if (!a || typeof a !== 'object') return a;
    const id = a.id != null && String(a.id).trim() !== '' ? String(a.id) : '';
    if (id && !seen.has(id)) {
      seen.add(id);
      return a;
    }
    changed = true;
    let nid = lineId ? `${lineId}:acc:${i}` : newAccessoryRowId();
    while (seen.has(nid)) nid = newAccessoryRowId();
    seen.add(nid);
    return { ...a, id: nid };
  });
  return changed ? out : accessories;
}
